import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, MessageSquare } from 'lucide-react';

const Contact = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    sujet: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [focusedField, setFocusedField] = useState('');

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitted(true);
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({
        nom: '',
        email: '',
        telephone: '',
        sujet: '',
        message: ''
      });
    }, 4000);
  };

  const styles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(to bottom, #000000, #0a0a0a)',
      color: '#ffffff',
      paddingTop: isMobile ? '2rem' : '3rem',
      paddingBottom: isMobile ? '3rem' : '5rem',
    },
    content: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: isMobile ? '0 1.5rem' : '0 2rem',
    },
    hero: {
      textAlign: 'center',
      marginBottom: isMobile ? '3rem' : '5rem',
      animation: 'fadeInDown 0.8s ease-out',
    },
    title: {
      fontSize: isMobile ? '2.5rem' : '4rem',
      fontWeight: '800',
      background: 'linear-gradient(135deg, #c59b00 0%, #ffd866 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      marginBottom: '1rem',
      fontFamily: "'Montserrat', sans-serif",
      letterSpacing: '2px',
    },
    subtitle: {
      fontSize: isMobile ? '1.1rem' : '1.3rem',
      color: '#cccccc',
      maxWidth: '700px',
      margin: '0 auto',
      lineHeight: '1.8',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
      gap: isMobile ? '2rem' : '3rem',
      marginBottom: isMobile ? '3rem' : '4rem',
    },
    contactInfoSection: {
      animation: 'fadeInLeft 0.8s ease-out',
    },
    formSection: {
      animation: 'fadeInRight 0.8s ease-out',
    },
    sectionTitle: {
      fontSize: isMobile ? '1.8rem' : '2.2rem',
      fontWeight: '700',
      color: '#ffffff',
      marginBottom: '2rem',
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
    },
    contactCard: {
      background: 'rgba(255, 255, 255, 0.03)',
      border: '2px solid rgba(197, 155, 0, 0.15)',
      borderRadius: '20px',
      padding: isMobile ? '1.5rem' : '2rem',
      marginBottom: '1.5rem',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      cursor: 'pointer',
      display: 'block',
      textDecoration: 'none',
    },
    iconWrapper: {
      background: 'linear-gradient(135deg, #c59b00 0%, #ffd866 100%)',
      width: isMobile ? '50px' : '60px',
      height: isMobile ? '50px' : '60px',
      borderRadius: '15px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '1rem',
      transition: 'transform 0.4s ease',
      boxShadow: '0 4px 15px rgba(197, 155, 0, 0.25)',
    },
    cardTitle: {
      fontSize: isMobile ? '1.2rem' : '1.4rem',
      fontWeight: '600',
      color: '#c59b00',
      marginBottom: '0.5rem',
    },
    cardContent: {
      fontSize: isMobile ? '1rem' : '1.1rem',
      color: '#cccccc',
      lineHeight: '1.8',
    },
    form: {
      background: 'rgba(255, 255, 255, 0.03)',
      border: '2px solid rgba(197, 155, 0, 0.15)',
      borderRadius: '20px',
      padding: isMobile ? '2rem' : '3rem',
    },
    formGroup: {
      marginBottom: '1.5rem',
    },
    label: {
      display: 'block',
      fontSize: '1rem',
      fontWeight: '600',
      color: '#ffffff',
      marginBottom: '0.5rem',
    },
    input: {
      width: '100%',
      padding: '1rem',
      borderRadius: '12px',
      border: '2px solid rgba(197, 155, 0, 0.22)',
      background: 'rgba(255, 255, 255, 0.05)',
      color: '#ffffff',
      fontSize: '1rem',
      outline: 'none',
      transition: 'all 0.3s ease',
      fontFamily: "'Poppins', sans-serif",
      boxSizing: 'border-box',
    },
    textarea: {
      width: '100%',
      padding: '1rem',
      borderRadius: '12px',
      border: '2px solid rgba(197, 155, 0, 0.22)',
      background: 'rgba(255, 255, 255, 0.05)',
      color: '#ffffff',
      fontSize: '1rem',
      outline: 'none',
      transition: 'all 0.3s ease',
      fontFamily: "'Poppins', sans-serif",
      minHeight: '150px',
      resize: 'vertical',
      boxSizing: 'border-box',
    },
    submitButton: {
      width: '100%',
      background: 'linear-gradient(135deg, #c59b00 0%, #ffd866 100%)',
      border: 'none',
      borderRadius: '12px',
      padding: '1.2rem',
      color: '#000000',
      fontWeight: '700',
      fontSize: '1.1rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.8rem',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(197, 155, 0, 0.25)',
    },
    successMessage: {
      background: 'rgba(76, 175, 80, 0.15)',
      border: '2px solid #4caf50',
      borderRadius: '15px',
      padding: '1.5rem',
      color: '#4caf50',
      textAlign: 'center',
      fontWeight: '600',
      fontSize: '1.1rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.8rem',
      animation: 'slideIn 0.5s ease-out',
    },
    mapSection: {
      marginTop: isMobile ? '3rem' : '4rem',
      animation: 'fadeInUp 0.8s ease-out 0.3s backwards',
    },
    mapContainer: {
      background: 'rgba(255, 255, 255, 0.03)',
      border: '2px solid rgba(197, 155, 0, 0.15)',
      borderRadius: '20px',
      padding: isMobile ? '1.5rem' : '2rem',
      overflow: 'hidden',
    },
    mapFrame: {
      width: '100%',
      height: isMobile ? '300px' : '450px',
      border: 'none',
      borderRadius: '15px',
    },
  };

  const ContactCard = ({ icon: Icon, title, content, href, hoverColor }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <a
        href={href}
        style={{
          ...styles.contactCard,
          transform: isHovered ? 'translateY(-10px) scale(1.02)' : 'translateY(0) scale(1)',
          borderColor: isHovered ? '#c59b00' : 'rgba(197, 155, 0, 0.15)',
          background: isHovered ? 'rgba(197, 155, 0, 0.08)' : 'rgba(255, 255, 255, 0.03)',
          boxShadow: isHovered ? '0 12px 30px rgba(197, 155, 0, 0.25)' : 'none',
          textDecoration: 'none',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div
          style={{
            ...styles.iconWrapper,
            transform: isHovered ? 'rotate(360deg) scale(1.1)' : 'rotate(0deg) scale(1)',
          }}
        >
          <Icon size={isMobile ? 24 : 28} color="#000000" />
        </div>
        <h3 style={styles.cardTitle}>{title}</h3>
        <p style={styles.cardContent}>{content}</p>
      </a>
    );
  };

  return (
    <>
      <style>
        {`
          @keyframes fadeInDown {
            from {
              opacity: 0;
              transform: translateY(-30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes fadeInLeft {
            from {
              opacity: 0;
              transform: translateX(-30px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          @keyframes fadeInRight {
            from {
              opacity: 0;
              transform: translateX(30px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes slideIn {
            from {
              opacity: 0;
              transform: scale(0.9);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
        `}
      </style>
      <div style={styles.container}>
        <div style={styles.content}>
          <div style={styles.hero}>
            <h1 style={styles.title}>CONTACTEZ-NOUS</h1>
            <p style={styles.subtitle}>
              Une question ? Un devis personnalisé ? Notre équipe est à votre écoute pour répondre à tous vos besoins en produits d'hygiène de qualité.
            </p>
          </div>

          <div style={styles.grid}>
            <div style={styles.contactInfoSection}>
              <h2 style={styles.sectionTitle}>
                <MessageSquare size={isMobile ? 28 : 32} color="#c59b00" />
                Nos Coordonnées
              </h2>

              <ContactCard
                icon={Phone}
                title="Téléphone"
                content="+33 6 44 66 35 83"
                href="tel:+33644663583"
              />

              <ContactCard
                icon={Mail}
                title="Email"
                content="destock.max@outlook.com"
                href="mailto:destock.max@outlook.com"
              />

              <ContactCard
                icon={MapPin}
                title="Adresse"
                content="4 Rue Du Bouguen, Brest, France"
                href="https://maps.google.com/?q=4+Rue+Du+Bouguen+Brest"
              />

              <ContactCard
                icon={Clock}
                title="Horaires d'ouverture"
                content="Lundi - Vendredi : 9h00 - 18h00 | Samedi : 10h00 - 16h00 | Dimanche : Fermé"
                href="#"
              />
            </div>

            <div style={styles.formSection}>
              <h2 style={styles.sectionTitle}>
                <Send size={isMobile ? 28 : 32} color="#c59b00" />
                Envoyez-nous un message
              </h2>

              {!isSubmitted ? (
                <form style={styles.form} onSubmit={handleSubmit}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Nom complet *</label>
                    <input
                      type="text"
                      name="nom"
                      value={formData.nom}
                      onChange={handleChange}
                      required
                      style={{
                        ...styles.input,
                        borderColor: focusedField === 'nom' ? '#c59b00' : 'rgba(197, 155, 0, 0.22)',
                        background: focusedField === 'nom' ? 'rgba(197, 155, 0, 0.06)' : 'rgba(255, 255, 255, 0.05)',
                      }}
                      onFocus={() => setFocusedField('nom')}
                      onBlur={() => setFocusedField('')}
                      placeholder="Votre nom"
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      style={{
                        ...styles.input,
                        borderColor: focusedField === 'email' ? '#c59b00' : 'rgba(197, 155, 0, 0.22)',
                        background: focusedField === 'email' ? 'rgba(197, 155, 0, 0.06)' : 'rgba(255, 255, 255, 0.05)',
                      }}
                      onFocus={() => setFocusedField('email')}
                      onBlur={() => setFocusedField('')}
                      placeholder="votre.email@exemple.com"
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Téléphone</label>
                    <input
                      type="tel"
                      name="telephone"
                      value={formData.telephone}
                      onChange={handleChange}
                      style={{
                        ...styles.input,
                        borderColor: focusedField === 'telephone' ? '#c59b00' : 'rgba(197, 155, 0, 0.22)',
                        background: focusedField === 'telephone' ? 'rgba(197, 155, 0, 0.06)' : 'rgba(255, 255, 255, 0.05)',
                      }}
                      onFocus={() => setFocusedField('telephone')}
                      onBlur={() => setFocusedField('')}
                      placeholder="+33 6 00 00 00 00"
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Sujet *</label>
                    <input
                      type="text"
                      name="sujet"
                      value={formData.sujet}
                      onChange={handleChange}
                      required
                      style={{
                        ...styles.input,
                        borderColor: focusedField === 'sujet' ? '#c59b00' : 'rgba(197, 155, 0, 0.22)',
                        background: focusedField === 'sujet' ? 'rgba(197, 155, 0, 0.06)' : 'rgba(255, 255, 255, 0.05)',
                      }}
                      onFocus={() => setFocusedField('sujet')}
                      onBlur={() => setFocusedField('')}
                      placeholder="Objet de votre message"
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Message *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      style={{
                        ...styles.textarea,
                        borderColor: focusedField === 'message' ? '#c59b00' : 'rgba(197, 155, 0, 0.22)',
                        background: focusedField === 'message' ? 'rgba(197, 155, 0, 0.06)' : 'rgba(255, 255, 255, 0.05)',
                      }}
                      onFocus={() => setFocusedField('message')}
                      onBlur={() => setFocusedField('')}
                      placeholder="Décrivez votre demande en détail..."
                    />
                  </div>

                  <button
                    type="submit"
                    style={styles.submitButton}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-3px) scale(1.02)';
                      e.currentTarget.style.boxShadow = '0 8px 25px rgba(197, 155, 0, 0.45)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0) scale(1)';
                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(197, 155, 0, 0.25)';
                    }}
                  >
                    <Send size={20} />
                    Envoyer le message
                  </button>
                </form>
              ) : (
                <div style={styles.successMessage}>
                  <CheckCircle size={28} />
                  Message envoyé avec succès ! Nous vous répondrons dans les plus brefs délais.
                </div>
              )}
            </div>
          </div>

          <div style={styles.mapSection}>
            <h2 style={styles.sectionTitle}>
              <MapPin size={isMobile ? 28 : 32} color="#c59b00" />
              Notre Emplacement
            </h2>
            <div style={styles.mapContainer}>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2675.8881234567!2d-4.485!3d48.384!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a2247c7d1d1d1d1%3A0xd1d1d1d1d1d1d1d1!2s4%20Rue%20Du%20Bouguen%2C%20Brest!5e0!3m2!1sfr!2sfr!4v1234567890"
                style={styles.mapFrame}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Localisation Destockmax"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;